import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Users, Flame, TrendingUp } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

const log = createLogger('FRIENDSTREAKWIDGET');

interface FriendStreak {
  current_streak: number;
  longest_streak: number;
  last_contribution_date: string | null;
  contributing_friends: string[];
}

export default function FriendStreakWidget() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [streak, setStreak] = useState<FriendStreak | null>(null);
  const [friendCount, setFriendCount] = useState(0);

  useEffect(() => {
    loadStreak();
  }, [profile]);

  const loadStreak = async () => {
    if (!profile) return;

    try {
      const { data: streakData } = await supabase
        .from('friend_streaks')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();

      if (streakData) {
        setStreak(streakData);
      }

      const { count, error: friendError } = await supabase
        .from('friendships')
        .select('*', { count: 'exact', head: true })
        .or(`user_id.eq.${profile.id},friend_id.eq.${profile.id}`)
        .eq('status', 'accepted');

      if (!friendError) {
        setFriendCount(count || 0);
      }
    } catch {
      // Silently handle missing tables or network errors
    }
  };

  if (!streak || friendCount === 0) {
    return null;
  }

  return (
    <TouchableOpacity
      style={[styles.container, { backgroundColor: theme.colors.surface }]}
      onPress={() => router.push('/(tabs)/friends')}
    >
      <LinearGradient
        colors={['#10B981', '#059669']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.iconContainer}
      >
        <Users size={24} color={theme.colors.text} />
      </LinearGradient>

      <View style={styles.content}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: theme.colors.text }]}>Friend Streak</Text>
          {streak.current_streak > 0 && (
            <View style={styles.streakBadge}>
              <Flame size={14} color="#F59E0B" />
              <Text style={styles.streakText}>{streak.current_streak}</Text>
            </View>
          )}
        </View>

        {streak.current_streak > 0 ? (
          <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
            You and your friends are on a {streak.current_streak} week streak!
          </Text>
        ) : (
          <Text style={[styles.description, { color: theme.colors.textSecondary }]}>
            Log workouts with friends to build your streak
          </Text>
        )}

        {streak.longest_streak > 0 && (
          <View style={styles.stats}>
            <TrendingUp size={14} color={theme.colors.textTertiary} />
            <Text style={[styles.statsText, { color: theme.colors.textTertiary }]}>
              Best: {streak.longest_streak} weeks
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    padding: 16,
    borderRadius: 2,
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    gap: 6,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
  },
  streakBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 2,
  },
  streakText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#F59E0B',
  },
  description: {
    fontSize: 14,
    lineHeight: 18,
  },
  stats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statsText: {
    fontSize: 12,
  },
});
